import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { Badge, ErrorNote, Field } from '../components/ui';
import { PageHeader } from '../layout/AppShell';
import { api } from '../lib/api';
import { dec, fmtDate } from '../lib/format';
import { STATUS_LABELS, type Project, type ProjectListItem } from '../lib/types';

const STATUS_TONE = { DRAFT: 'neutral', IN_PROGRESS: 'info', DONE: 'ok', ARCHIVED: 'warn' } as const;

export function ProjectsPage() {
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [params] = useSearchParams();
  const [creating, setCreating] = useState(!!params.get('nouveau'));
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sample = useMutation({
    mutationFn: () => api<Project>('/samples/abd', { method: 'POST' }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projets/${p.id}`);
    },
  });
  const demo = useMutation({
    mutationFn: () => api<Project>('/samples/immeuble-r4', { method: 'POST' }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projets/${p.id}?onglet=dqe`);
    },
  });
  const projects = useQuery({
    queryKey: ['projects', q, showArchived],
    queryFn: () => api<ProjectListItem[]>(`/projects?${new URLSearchParams({ ...(q ? { q } : {}), ...(showArchived ? { includeArchived: 'true' } : {}) })}`),
  });

  return (
    <>
      <PageHeader
        title="Mes projets"
        subtitle="Projet → zones → niveaux → types d'éléments → quantités"
        actions={
          can('project:create') && (
            <div className="flex flex-wrap gap-2">
              {can('project:edit') && <Link className="btn-secondary" to="/import">Importer Excel / PDF</Link>}
              {can('project:edit') && <Link className="btn-secondary" to="/plans">Analyser un plan</Link>}
              <button className="btn-secondary" onClick={() => demo.mutate()} disabled={demo.isPending}>
                {demo.isPending ? 'Préparation…' : 'Devis exemple R+4'}
              </button>
              <button className="btn-secondary" onClick={() => sample.mutate()} disabled={sample.isPending}>
                {sample.isPending ? 'Chargement…' : "Charger l'exemple Abdelatif"}
              </button>
              <button className="btn-primary" onClick={() => setCreating((c) => !c)}>{creating ? 'Fermer' : '+ Nouveau projet'}</button>
            </div>
          )
        }
      />
      {(sample.error ?? demo.error) && <div className="mb-4"><ErrorNote error={sample.error ?? demo.error} /></div>}
      {creating && <NewProjectForm onDone={() => setCreating(false)} />}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input className="input max-w-sm" placeholder="Rechercher (nom, référence, client)…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-2 text-sm text-muted">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Afficher les archivés
        </label>
      </div>
      <section className="card overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Projet</th>
              <th>Client</th>
              <th className="num">Niveaux</th>
              <th className="num">Types</th>
              <th>Statut</th>
              <th>Modifié le</th>
            </tr>
          </thead>
          <tbody>
            {projects.data?.map((p) => (
              <tr key={p.id} className="hover:bg-surface">
                <td className="font-mono text-xs">{p.reference}</td>
                <td>
                  <Link to={`/projets/${p.id}`} className="font-semibold text-tech-600 hover:underline">{p.name}</Link>
                  {p.location && <div className="text-xs text-muted">{p.location}</div>}
                </td>
                <td>{p.clientName ?? '—'}</td>
                <td className="num">{p.levelCount}</td>
                <td className="num">{p.typeCount}</td>
                <td><Badge tone={STATUS_TONE[p.status]}>{STATUS_LABELS[p.status]}</Badge></td>
                <td className="text-muted">{fmtDate(p.updatedAt)}</td>
              </tr>
            ))}
            {projects.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-muted">Aucun projet. Créez le premier avec « + Nouveau projet ».</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </>
  );
}

function NewProjectForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: '',
    clientName: '',
    location: "N'Djamena",
    buildingType: 'Habitation',
    foundation: true,
    roof: true,
    upperLevels: '1',
    groundHeight: '3.20',
    upperHeight: '3.00',
    vatEnabled: false,
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: () =>
      api<Project>('/projects', {
        method: 'POST',
        body: {
          name: f.name,
          clientName: f.clientName || undefined,
          location: f.location || undefined,
          buildingType: f.buildingType || undefined,
          settings: { vatEnabled: f.vatEnabled },
          template: { foundation: f.foundation, roof: f.roof, upperLevels: Number(f.upperLevels), groundHeight: dec(f.groundHeight), upperHeight: dec(f.upperHeight) },
        },
      }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      onDone();
      navigate(`/projets/${p.id}`);
    },
  });

  return (
    <form
      className="card mb-6 grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="md:col-span-2"><Field label="Nom du projet"><input className="input" required value={f.name} onChange={set('name')} placeholder="Maison R+1 — N'Djamena" /></Field></div>
      <Field label="Client"><input className="input" value={f.clientName} onChange={set('clientName')} /></Field>
      <Field label="Localisation"><input className="input" value={f.location} onChange={set('location')} /></Field>
      <Field label="Type de bâtiment"><input className="input" value={f.buildingType} onChange={set('buildingType')} /></Field>
      <Field label="Étages au-dessus du RDC" hint="0 = RDC seul ; 1 = R+1…"><input className="input" type="number" min={0} max={60} value={f.upperLevels} onChange={set('upperLevels')} /></Field>
      <Field label="Hauteur du RDC (m)"><input className="input" inputMode="decimal" value={f.groundHeight} onChange={set('groundHeight')} /></Field>
      <Field label="Hauteur des étages (m)" hint="Modifiable ensuite niveau par niveau"><input className="input" inputMode="decimal" value={f.upperHeight} onChange={set('upperHeight')} /></Field>
      <div className="flex flex-wrap items-center gap-4 text-sm md:col-span-2 xl:col-span-3">
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.foundation} onChange={(e) => setF((s) => ({ ...s, foundation: e.target.checked }))} /> Niveau Fondations</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.roof} onChange={(e) => setF((s) => ({ ...s, roof: e.target.checked }))} /> Niveau Toiture</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.vatEnabled} onChange={(e) => setF((s) => ({ ...s, vatEnabled: e.target.checked }))} /> Appliquer la TVA (taux par défaut de l'entreprise, modifiable dans le projet)</label>
      </div>
      <div className="flex items-end"><button className="btn-primary w-full" disabled={create.isPending}>Créer le projet</button></div>
      {create.error && <div className="md:col-span-2 xl:col-span-4"><ErrorNote error={create.error} /></div>}
    </form>
  );
}
